import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Link from '../components/SafeLink';
import Api from '../services/api';
import { useAuth } from '../store/Auth';
import { usePage, useResource } from '../hooks/useResource';
import { T, useText } from '../components/Text';
import { Connection } from '../components/Layout';
import OrderEditor from '../components/OrderEditor';

/**
 * Siparişler Sayfası (/orders.html, /orders)
 * Sipariş listeleme, gelişmiş filtreler, takvim seçici, toplu seçim/silme ve sipariş detay modalı.
 */
export default function Orders() {
  const { user } = useAuth();
  const { t, locale } = useText();
  const route = useLocation();

  const manager = Api.isManager(user);
  const writable = Api.canManageBranch(user);
  usePage(t(manager ? 'Siparişler' : 'Siparişlerim'), 'admin-page orders-page');

  // Filtreler & Arama
  const [filters, setFilters] = useState(() => ({
    customer: new URLSearchParams(route.search).get('customer') || '',
    date: Api.today(),
    karat: '',
    color: '',
    status: '',
  }));
  const orderParams=new URLSearchParams();
  if(filters.date) orderParams.set('date',filters.date);
  const linkedId=new URLSearchParams(route.search).get('open');
  if(linkedId) orderParams.set('open',linkedId);
  const orderQuery=orderParams.toString();
  useLayoutEffect(()=>Api.subscribeOrdersDate(filters.date),[filters.date,user?.uid,user?.branchId]);
  const resource=useResource('/api/orders'+(orderQuery?'?'+orderQuery:''));
  const customerResource=useResource(manager?'/api/customer-options':null,['customers-changed']);
  const orders=resource.data?.orders || [];
  const customers=customerResource.data?.customers || [];
  const [search, setSearch] = useState('');
  const [largeMode, setLargeMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [calendarMonth, setCalendarMonth] = useState(Api.today().slice(0, 7));

  // Sipariş Detay Modalı ve Sıra Gezinimi
  const [detail, setDetail] = useState(null);
  const [queue, setQueue] = useState([]);
  const [detailBusy, setDetailBusy] = useState(false);

  // Silme Onay Modalı
  const [deleting, setDeleting] = useState(false);
  const [deletion, setDeletion] = useState(null);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);

  const deletionLock = useRef(false);
  const requested = useRef('');

  // Arama ve Filtreleme
  const query = search.trim().toLocaleLowerCase('tr-TR');
  const filtered = orders
    .filter((o) => {
      const matchSearch = [o.id, o.customerName, o.customerUsername, o.createdBy, o.karat, o.color]
        .join(' ')
        .toLocaleLowerCase('tr-TR')
        .includes(query);

      const matchCustomer =
        !filters.customer ||
        (filters.customer === 'unassigned'
          ? o.customerId === null
          : String(o.customerId) === filters.customer);

      const matchDate = !filters.date || o.date === filters.date;
      const matchKarat = !filters.karat || o.karat === filters.karat;
      const matchColor = !filters.color || o.color === filters.color;
      const matchStatus =
        !filters.status || (filters.status === 'ready' ? o.completed : !o.completed);

      return matchSearch && matchCustomer && matchDate && matchKarat && matchColor && matchStatus;
    })
    .sort(
      (a, b) =>
        Number(b.customerId === null) - Number(a.customerId === null) ||
        b.date.localeCompare(a.date) ||
        b.id - a.id
    );

  const ids = filtered.map((o) => o.id);
  const idsKey = ids.join(',');

  useEffect(() => {
    setSelected((prev) => new Set([...prev].filter((id) => ids.includes(id))));
  }, [idsKey]);

  useEffect(() => {
    if (detail && resource.data && !orders.some((o) => o.id === detail.id)) {
      setDetail(null);
    }
  }, [resource.data, orders, detail]);

  // URL'den `open=ID` ile doğrudan sipariş açma
  useEffect(() => {
    const params = new URLSearchParams(route.search);
    const id = Number(params.get('open'));
    if (id && resource.data && requested.current !== route.search && orders.some((o) => o.id === id)) {
      requested.current = route.search;
      const raw = params.get('queue') || '';
      const q = /^[\d,]+$/.test(raw)
        ? [...new Set(raw.split(',').map(Number))].filter((orderId) => orders.some((o) => o.id === orderId))
        : ids;
      if (!q.includes(id)) q.unshift(id);
      setQueue(q);
      setDetail(orders.find((o) => o.id === id));
    }
  }, [route.search, resource.data, orders, ids]);

  const current = detail ? orders.find((o) => o.id === detail.id) : null;
  const validQueue = queue.filter((id) => orders.some((o) => o.id === id));
  const currentIndex = detail ? validQueue.indexOf(detail.id) : -1;

  const formatDate = (val) =>
    val ? new Date(`${val}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === 'date' && !value && !manager ? Api.today() : value,
    }));
    if (key === 'date' && value) {
      setCalendarMonth(value.slice(0, 7));
    }
  };

  const handleOpenDetail = (id, newQueue = ids) => {
    if (detailBusy || deleting) return;
    setQueue(newQueue);
    setDetail(orders.find((o) => o.id === id));
  };

  const handleOrderSaved = async (savedOrder, advance) => {
    const nextId = validQueue[currentIndex + 1];
    setDetail(savedOrder);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
    await resource.refresh();
    if (advance) {
      if (nextId) setDetail(orders.find((o) => o.id === nextId));
      else setDetail(null);
    }
  };

  // Sipariş Silme (Tekli veya Toplu)
  const handleDeleteOrders = async () => {
    if (deletionLock.current || !deletion) return;
    deletionLock.current = true;
    setDeleting(true);
    setError('');

    try {
      const { records, single } = deletion;
      const url = single ? `/api/orders/${records[0].id}` : '/api/orders/delete-batch';
      const method = single ? 'DELETE' : 'POST';
      const body = single
        ? { version: records[0].version }
        : { orders: records.map((o) => ({ id: o.id, version: o.version })) };

      await Api.mutate(url, method, body);
      setSelected((prev) => new Set([...prev].filter((id) => !records.some((o) => o.id === id))));
      if (detail && records.some((o) => o.id === detail.id)) setDetail(null);
      setDeletion(null);
      await resource.refresh();
    } catch (failure) {
      setError(failure.message);
      setDeletion(null);
    } finally {
      deletionLock.current = false;
      setDeleting(false);
    }
  };

  const handleSelectAll = () => {
    setSelected((prev) => (ids.length && ids.every((id) => prev.has(id)) ? new Set() : new Set(ids)));
  };

  // Takvim Ayı Hesaplamaları
  const monthDate = new Date(`${calendarMonth}-01T12:00:00`);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const offset = (monthDate.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const handleShiftMonth = (amount) => {
    const d = new Date(year, month + amount, 1, 12);
    setCalendarMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <main id="main" className="orders-main">
      {/* Üst Başlık ve Yeni Sipariş Butonu */}
      <div className="orders-heading">
        <div>
          <div className="eyebrow">
            <T>ÜRETİM TAKİBİ</T>
          </div>
          <h1 id="orders-title">
            <T>{manager ? 'Siparişler' : 'Siparişlerim'}</T>
          </h1>
        </div>

        <div className="heading-actions">
          <span className="connection-status">
            <Connection />
          </span>
          {writable && (
            <Link className="button gold" id="create-order-link" to="/order-create">
              <T>Sipariş Oluştur ↗</T>
            </Link>
          )}
        </div>
      </div>

      {(error || resource.error || customerResource.error) && (
        <p className="form-error" role="alert">
          <T>{error || resource.error || customerResource.error}</T>
        </p>
      )}

      {/* Arama, Toplu İşlemler ve Görünüm Seçenekleri */}
      <div className="orders-toolbar">
        <label className="orders-search">
          ⌕{' '}
          <input
            id="order-search"
            type="search"
            placeholder={t('Sipariş, müşteri veya kullanıcı ara…')}
            aria-label={t('Sipariş ara')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        {writable && (
          <div className="selection-tools" id="selection-tools">
            <button
              type="button"
              id="select-all"
              disabled={!ids.length || deleting}
              onClick={handleSelectAll}
            >
              <T>{ids.length && ids.every((id) => selected.has(id)) ? 'Tüm seçimi kaldır' : 'Tümünü seç'}</T>
            </button>
            <button
              type="button"
              id="clear-selection"
              disabled={!selected.size || deleting}
              onClick={() => setSelected(new Set())}
            >
              <T>Seçimi kaldır</T>
            </button>
            <button
              type="button"
              id="delete-selected"
              className="danger-button"
              disabled={!selected.size || deleting}
              onClick={() =>
                setDeletion({
                  records: orders.filter((o) => selected.has(o.id)),
                  single: false,
                })
              }
            >
              <T>Seçilenleri sil</T>
            </button>
          </div>
        )}

        <div className="view-options" role="group" aria-label={t('Görünüm')}>
          <button
            type="button"
            id="grid-view"
            aria-label={t('Kart görünümü')}
            title={t('Kart görünümü')}
            aria-pressed={!largeMode}
            className={!largeMode ? 'selected' : ''}
            onClick={() => setLargeMode(false)}
          >
            ▦
          </button>
          <button
            type="button"
            id="large-view"
            aria-label={t('Büyük kart görünümü')}
            title={t('Büyük kart görünümü')}
            aria-pressed={largeMode}
            className={largeMode ? 'selected' : ''}
            onClick={() => setLargeMode(true)}
          >
            ▣
          </button>
        </div>
      </div>

      <div className="orders-layout">
        {/* Sol Panel: Filtreler ve Takvim */}
        <aside className="order-filters" aria-label={t('Sipariş filtreleri')}>
          <div className="filters-heading">
            <h2>
              <T>Filtreler</T>
            </h2>
            <button
              type="button"
              id="clear-filters"
              onClick={() => {
                setSearch('');
                setFilters({
                  customer: '',
                  date: manager ? '' : Api.today(),
                  karat: '',
                  color: '',
                  status: '',
                });
                setCalendarMonth(Api.today().slice(0, 7));
              }}
            >
              <T>Temizle ↺</T>
            </button>
          </div>

          {/* Müşteri Filtresi (Yalnızca Yönetici) */}
          {manager && (
            <label className="filter-field" id="customer-filter-label">
              <T>Müşteri</T>
              <select
                id="filter-customer"
                value={filters.customer}
                onChange={(e) => handleFilterChange('customer', e.target.value)}
              >
                <option value="">{t('Tüm müşteriler')}</option>
                <option value="unassigned">{t('Müşteri Seçilmedi')}</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Tarih ve Takvim */}
          <div className="filter-field">
            <label htmlFor="filter-date">
              <T>Tarih</T>
            </label>
            <input
              type="date"
              id="filter-date"
              aria-label={t('Sipariş tarihi')}
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
            />

            <div className="calendar">
              <div className="calendar-heading">
                <button
                  type="button"
                  id="previous-month"
                  aria-label={t('Önceki ay')}
                  onClick={() => handleShiftMonth(-1)}
                >
                  ‹
                </button>
                <span id="calendar-month">
                  {monthDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
                </span>
                <button
                  type="button"
                  id="next-month"
                  aria-label={t('Sonraki ay')}
                  onClick={() => handleShiftMonth(1)}
                >
                  ›
                </button>
              </div>

              <div className="calendar-weekdays" aria-hidden="true">
                <span>P</span>
                <span>S</span>
                <span>Ç</span>
                <span>P</span>
                <span>C</span>
                <span>C</span>
                <span>P</span>
              </div>

              <div className="calendar-days" id="calendar-days">
                {Array.from({ length: offset }, (_, i) => (
                  <span key={`gap-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const dayStr = `${calendarMonth}-${String(i + 1).padStart(2, '0')}`;
                  const isToday = dayStr === Api.today();
                  const isSelected = dayStr === filters.date;

                  return (
                    <button
                      key={dayStr}
                      type="button"
                      className={`${isToday ? 'today ' : ''}${isSelected ? 'selected' : ''}`}
                      aria-label={formatDate(dayStr)}
                      aria-pressed={isSelected}
                      onClick={() =>
                        handleFilterChange('date', manager && filters.date === dayStr ? '' : dayStr)
                      }
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Ayar Filtresi */}
          <label className="filter-field">
            <T>Ayar</T>
            <select
              id="filter-karat"
              value={filters.karat}
              onChange={(e) => handleFilterChange('karat', e.target.value)}
            >
              <option value="">{t('Tüm ayarlar')}</option>
              <option value="14">{t('14 Ayar')}</option>
              <option value="18">{t('18 Ayar')}</option>
              <option value="8">{t('8 Ayar')}</option>
              <option value="22">{t('22 Ayar')}</option>
              <option value="21">{t('21 Ayar')}</option>
              <option value="10">{t('10 Ayar')}</option>
              <option value="9">{t('9 Ayar')}</option>
              <option value="Gümüş">{t('Gümüş')}</option>
            </select>
          </label>

          {/* Renk Filtresi */}
          <label className="filter-field">
            <T>Renk</T>
            <select
              id="filter-color"
              value={filters.color}
              onChange={(e) => handleFilterChange('color', e.target.value)}
            >
              <option value="">{t('Tüm renkler')}</option>
              <option value="Beyaz">{t('Beyaz')}</option>
              <option value="Yeşil">{t('Yeşil')}</option>
              <option value="Roz">{t('Roz')}</option>
              <option value="Diğer">{t('Diğer')}</option>
            </select>
          </label>

          {/* Tamamlanma Durumu */}
          <label className="filter-field">
            <T>Tamamlanma durumu</T>
            <select
              id="filter-status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">{t('Tümü')}</option>
              <option value="ready">{t('Teslime hazır')}</option>
              <option value="ongoing">{t('İşlemde')}</option>
            </select>
          </label>
        </aside>

        {/* Sağ Panel: Sipariş Kartları Izgarası */}
        <section className="orders-results" aria-label={t('Sipariş kayıtları')}>
          <div className="results-heading">
            <span id="order-count" role="status">
              <T>
                {`${filtered.length} sipariş · ${
                  filtered.filter((o) => o.completed).length
                } hazır`}
              </T>
            </span>
            {selected.size > 0 && (
              <span id="selection-count" role="status">
                <T>{`${selected.size} sipariş seçildi`}</T>
              </span>
            )}
            <span>
              <T>Fotoğrafa tıklayarak detayları açın.</T>
            </span>
          </div>

          <div className={`orders-grid ${largeMode ? 'large-mode' : ''}`} id="orders-grid">
            {filtered.map((order) => {
              const isSelected = selected.has(order.id);
              const isUnassigned = order.customerId === null;

              return (
                <article
                  key={order.id}
                  data-id={order.id}
                  className={`order-card ${isUnassigned ? 'unassigned ' : ''}${
                    isSelected ? 'is-selected' : ''
                  }`}
                >
                  {writable && (
                    <label className="order-select">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={deleting}
                        aria-label={t(`Sipariş #${order.id} seç`)}
                        onChange={(e) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(order.id);
                            else next.delete(order.id);
                            return next;
                          });
                        }}
                      />
                    </label>
                  )}

                  <button
                    type="button"
                    className="order-open"
                    aria-label={t(
                      `${order.customerName}, Sipariş #${order.id}, ${
                        order.completed ? 'Teslime hazır' : 'İşlemde'
                      } — detayları aç`
                    )}
                    onClick={() => handleOpenDetail(order.id)}
                  >
                    <img
                      className="order-image"
                      src={order.photoUrl}
                      alt={`${order.customerName} #${order.id}`}
                      loading="lazy"
                    />

                    <span className="order-card-body">
                      {manager && <span className="order-casting-id">id#{order.id}</span>}

                      <span className="order-name-line">
                        <span className={`order-customer ${isUnassigned ? 'unassigned-label' : ''}`}>
                          {isUnassigned ? <T>Müşteri Seçilmedi</T> : order.customerName}
                        </span>
                        <span className={`order-status ${order.completed ? 'ready' : ''}`}>
                          <T>{order.completed ? 'Hazır ✓' : 'İşlemde'}</T>
                        </span>
                      </span>

                      <span className="order-properties">
                        <T>
                          {`${
                            order.karat
                              ? order.karat === 'Gümüş'
                                ? 'Gümüş'
                                : `${order.karat} Ayar`
                              : 'Ayar bekliyor'
                          } · ${order.color || 'Renk bekliyor'}`}
                        </T>
                      </span>

                      <span className="order-date-line">
                        <span className="order-date">{formatDate(order.date)}</span>
                        {order.urgent && (
                          <span className="urgent-badge">
                            <T>Acil</T>
                          </span>
                        )}
                      </span>

                      <span className="order-creator">
                        <T>Oluşturan:</T> {order.createdBy}
                      </span>
                    </span>

                    <span className="order-card-footer">
                      <T>Detayları Gör ↗</T>
                    </span>
                  </button>
                </article>
              );
            })}
          </div>

          {resource.loading && !resource.data && <div className="orders-empty" role="status"><T>Siparişler yükleniyor…</T></div>}
          {!resource.loading && !resource.error && filtered.length === 0 && (
            <div className="orders-empty" id="orders-empty">
              <span>◇</span>
              <h2>
                <T>Sipariş bulunamadı.</T>
              </h2>
              <p>
                <T>Filtreleri temizleyin veya siparişlerin girilmesini bekleyin.</T>
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Sipariş Detay Modalı */}
      {detail && (
        <div
          className="dialog-backdrop"
          onClick={() => !detailBusy && !deleting && setDetail(null)}
        >
          <div
            className="order-dialog open"
            id="order-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialog-heading">
              <div>
                <div className="eyebrow">
                  <T>SİPARİŞ DETAYI</T>
                </div>
                <h2 id="detail-title">
                  <T>
                    {`${writable ? 'Siparişi düzenle' : 'Sipariş detayları'} · Döküm #${detail.id}`}
                  </T>
                </h2>
              </div>

              <div className="dialog-navigation">
                <button
                  id="previous-order"
                  type="button"
                  aria-label={t('Önceki sipariş')}
                  disabled={currentIndex <= 0 || detailBusy || deleting}
                  onClick={() => handleOpenDetail(validQueue[currentIndex - 1], validQueue)}
                >
                  ‹
                </button>
                <span id="detail-counter">
                  {`${currentIndex + 1} / ${validQueue.length}`}
                </span>
                <button
                  id="next-order"
                  type="button"
                  aria-label={t('Sonraki sipariş')}
                  disabled={
                    currentIndex < 0 || currentIndex >= validQueue.length - 1 || detailBusy || deleting
                  }
                  onClick={() => handleOpenDetail(validQueue[currentIndex + 1], validQueue)}
                >
                  ›
                </button>
                <button
                  id="close-detail"
                  className="detail-close"
                  type="button"
                  disabled={detailBusy || deleting}
                  onClick={() => setDetail(null)}
                >
                  <T>Kapat ×</T>
                </button>
              </div>
            </div>

            {writable && current && current.version !== detail.version && (
              <p className="dialog-live-note" role="status">
                <T>
                  Bu sipariş başka bir ekranda güncellendi. Son verileri görmek için kapatıp yeniden açın.
                </T>
              </p>
            )}

            <div id="edit-order-form">
              <OrderEditor
                key={`${detail.id}:${detail.version}`}
                order={detail}
                current={current}
                customers={customers}
                user={user}
                onSaved={handleOrderSaved}
                onBusy={setDetailBusy}
                onDelete={(o) => setDeletion({ records: [o], single: true })}
                last={currentIndex >= validQueue.length - 1}
              />
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modalı */}
      {deletion && (
        <div
          className="dialog-backdrop"
          onClick={() => !deleting && setDeletion(null)}
        >
          <div className="delete-dialog open" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow">
              <T>SİLME ONAYI</T>
            </div>
            <h2 id="delete-title">
              <T>Sipariş silinsin mi?</T>
            </h2>
            <p id="delete-description">
              <T>
                {deletion.records.length === 1
                  ? `Sipariş #${deletion.records[0].id} ve fotoğrafı silinecek. Müşteri panelinden de kaldırılacak. Bu işlem geri alınamaz.`
                  : `${deletion.records.length} seçili sipariş ve fotoğrafları silinecek. Müşteri panellerinden de kaldırılacak. Bu işlem geri alınamaz.`}
              </T>
            </p>
            <div className="delete-dialog-actions">
              <button
                type="button"
                id="cancel-delete"
                autoFocus
                disabled={deleting}
                onClick={() => setDeletion(null)}
              >
                <T>Vazgeç</T>
              </button>
              <button
                type="button"
                id="confirm-delete"
                className="danger-button"
                disabled={deleting}
                onClick={handleDeleteOrders}
              >
                <T>{deleting ? 'Siliniyor…' : 'Evet, sil'}</T>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bildirim Toast'u */}
      {showToast && (
        <div className="order-success-notice visible" role="status" aria-live="polite">
          <span aria-hidden="true">✓</span>
          <T> Sipariş Güncellendi</T>
        </div>
      )}
    </main>
  );
}
